import { request } from './api-client';
import {
  BrandDetailSchema,
  BrandSummarySchema,
  SenderIdentitySummarySchema,
  TopicSummarySchema,
  pagedSchema,
  type BreakerThresholds,
} from './schemas';

const BrandPageSchema = pagedSchema(BrandSummarySchema);

export function listBrands() {
  return request('/api/v1/brands?take=100', BrandPageSchema);
}

export type SaveTopicInput = {
  name: string;
  description: string;
  displayOrder: number;
  isActive: boolean;
};

/**
 * Create or update a topic.
 *
 * No delete, deliberately: recipients hold preferences against a topic and sent campaigns declared it,
 * so retiring (`isActive: false`) is the only removal — it takes the topic out of the preference centre
 * and the campaign picker while leaving every recorded answer intact.
 */
export function saveTopic(brandId: string, topicId: string | null, input: SaveTopicInput) {
  const path =
    topicId === null
      ? `/api/v1/brands/${brandId}/topics`
      : `/api/v1/brands/${brandId}/topics/${topicId}`;

  return request(path, TopicSummarySchema, { method: topicId === null ? 'POST' : 'PUT', body: input });
}

export function getBrand(brandId: string) {
  return request(`/api/v1/brands/${brandId}`, BrandDetailSchema);
}

/**
 * The authentication flags are absent from this type on purpose, mirroring the server's DTO: they
 * reflect DNS that SendGrid verified, and a form able to set one could send unsigned mail past a
 * checklist that believed it was fine.
 */
export type UpdateBrandInput = {
  name: string;
  fromDomain: string;
  systemFromDomain: string;
  defaultFromName: string;
  replyToAddress: string | null;
  ipPoolName: string | null;
  footerHtml: string;
  postalAddress: string;
  segmentationAttributes: string[];
  breakerThresholds: BreakerThresholds;
  /** Null clears the cap. Always sent, because absence cannot express "turn it off". */
  frequencyCap: { maxSends: number; windowDays: number } | null;
  requiresApproval: boolean;
  isActive: boolean;
};

export function updateBrand(brandId: string, input: UpdateBrandInput) {
  return request(`/api/v1/brands/${brandId}`, BrandDetailSchema, { method: 'PUT', body: input });
}

export type SaveSenderInput = {
  label: string;
  fromLocalPart: string;
  fromName: string;
  replyToAddress: string | null;
  isActive: boolean;
};

export function saveSender(input: { brandId: string; senderIdentityId?: string } & SaveSenderInput) {
  const { brandId, senderIdentityId, ...body } = input;

  return senderIdentityId === undefined
    ? request(`/api/v1/brands/${brandId}/senders`, SenderIdentitySummarySchema, {
        method: 'POST',
        body,
      })
    : request(`/api/v1/brands/${brandId}/senders/${senderIdentityId}`, SenderIdentitySummarySchema, {
        method: 'PUT',
        body,
      });
}
